// @ts-ignore
import * as d3 from 'd3';

interface GlobalTaxByYear {
    [year: string]: number;
}

export function drawGlobalTaxChart(selector: string, data: any, isCompact: boolean = false) {
    const chartContainer = document.getElementById(selector);
    if (!chartContainer) {
        console.error(`Element with selector '${selector}' not found.`);
        return;
    }

    chartContainer.textContent = "";
    chartContainer.style.removeProperty("display");
    chartContainer.style.removeProperty("align-items");
    chartContainer.style.removeProperty("justify-content");
    chartContainer.style.removeProperty("font-weight");
    
    // Scale factors for compact mode
    const fontScale = isCompact ? 0.7 : 1.0;
    const labelFontScale = isCompact ? 0.65 : 1.0;

    // Parse the data to a suitable format
    const dataset = Object.entries(data).map(([key, value]) => {
        return { year: Number.parseInt(key), value: Math.round(Number(value)) };
    });

    // console.debug(dataset);
    
    // Interpolate zero crossings for area fills
    const interpolatedDataset: { year: number, value: number }[] = [];
    for (let i = 0; i < dataset.length - 1; i++) {
        const current = dataset[i]; //{ year : dataset[i].year, value : dataset[i].value * 100 } //dataset[i];
        const next = dataset[i + 1]; //{ year : dataset[i+1].year, value : dataset[i+1].value * 100 } //dataset[i + 1];
        interpolatedDataset.push(current);

        // Check for zero crossing
        if ((current.value > 0 && next.value < 0) || (current.value < 0 && next.value > 0)) {
            const ratio = Math.abs(current.value) / (Math.abs(current.value) + Math.abs(next.value));
            const interpolatedYear = current.year + ratio * (next.year - current.year);
            const interpolatedValue = 0;
            interpolatedDataset.push({ year: interpolatedYear, value: interpolatedValue });
        }
    }
    // interpolatedDataset.push({ year : dataset[dataset.length - 1].year, value : dataset[dataset.length - 1].value * 100 })//(dataset[dataset.length - 1]);
    interpolatedDataset.push(dataset[dataset.length - 1]);

    // console.debug(interpolatedDataset);
    
    // Calculate min and max values for the y-axis
    let minValue = d3.min(interpolatedDataset, d => d.value);// - 4;
    let maxValue = d3.max(interpolatedDataset, d => d.value);// + 4;
    
    // Ensure min and max are not equal to avoid a single y tick line when all values are the same
    if (minValue == maxValue) {
        minValue -= 1000;
        maxValue += 1000;
    } else {
        // pad min and max by 5% of the range
        const range = maxValue - minValue;
        minValue -= range * 0.05;
        maxValue += range * 0.05;
    }

    // Get the full width and height of the container
    const fullWidth = chartContainer.offsetWidth;
    const fullHeight = chartContainer.offsetHeight;

    // Define margins (rem) and convert to pixels
    const margin = { top: 1, right: 1.5, bottom: 2, left: 4.5 };
    const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    margin.top = margin.top * remSize;
    margin.right = margin.right * remSize;
    margin.bottom = margin.bottom * remSize;
    margin.left = margin.left * remSize;

    // Calculate the effective width and height for the chart area
    const width = fullWidth - margin.left - margin.right;
    const height = fullHeight - margin.top - margin.bottom;

    let existingSvg = chartContainer.querySelector('svg');
    if (existingSvg) {
        existingSvg.remove();
    }
    
    // Append SVG object to the chartContainer
    const svg = d3.select(chartContainer)
        .append("svg")
        .attr("width", fullWidth)
        .attr("height", fullHeight)
        .style("font-family", "Open Sans")
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

    // Add X axis
    const x = d3.scaleLinear()
        .domain(d3.extent(interpolatedDataset, d => d.year))
        .range([0, width]);
    
    const xAxisGroup = svg.append("g")
        .attr("transform", `translate(0,${height})`)
        .call(d3
            .axisBottom(x)
            .ticks(interpolatedDataset.length)
            .tickValues(interpolatedDataset.map(d => d.year))
            .tickFormat(d3.format("d"))
            .tickPadding(remSize * 0.5));

    xAxisGroup.selectAll("text")
        .style("font-size", `${.85 * fontScale}rem`)
        .attr("font-family", "Open Sans")
        .attr("dy", "12px");

    xAxisGroup.select(".domain").remove();

    // Add Y axis
    const y = d3.scaleLinear()
        .domain([Math.min(0,minValue), Math.max(0,maxValue)])
        .range([height, 0])
        .nice(10);

    const yAxisGroup = svg.append("g")
        .call(d3.axisLeft(y).ticks(isCompact ? 5 : 10).tickSize(0).tickFormat(d => numberFormat(d)).tickPadding(remSize * 2));

    yAxisGroup.selectAll("text")
        .style("font-size", `${.65 * fontScale}rem`)

    yAxisGroup.select(".domain").remove();

    // Draw the horizontal grid lines
    svg.selectAll("line.horizontal-grid").data(y.ticks()).enter()
        .append("line")
        .attr("class", "horizontal-grid")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", d => y(d))
        .attr("y2", d => y(d))
        .attr("stroke", "#E8E8E8")
        .lower();
    
    // Emphasize the zero baseline
    svg.append("line")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", y(0))
        .attr("y2", y(0))
        .attr("stroke", "#E8E8E8")
        .attr("stroke-width", 2)
        .lower();

    svg.append("rect")
        .attr("width", width)
        .attr("height", height)
        .attr("fill", "white")
        .lower();

    // Add area under/above the line (fill to the zero baseline)
    svg.append("path")
        .datum(interpolatedDataset)
        .attr("fill", "rgba(220, 53, 69, 1)") // negative area color
        .attr("fill-opacity", 0.12)
        .attr("stroke", "none")
        .attr("d", d3.area()
            // .defined((d: any) => d.value < 0)
            .x((d: any) => x(d.year))
            .y0(() => y(0))
            .y1((d: any) => y(Math.max(0,d.value)))
        );
    
    svg.append("path")
        .datum(interpolatedDataset)
        .attr("fill", "rgba(38, 137, 13, 1)") // positive area color
        .attr("fill-opacity", 0.12)
        .attr("stroke", "none")
        .attr("d", d3.area()
            // .defined((d: any) => d.value >= 0)
            .x((d: any) => x(d.year))
            .y0(() => y(0))
            .y1((d: any) => y(Math.min(0,d.value)))
        );
    
    // Add the line
    svg.append("path")
        .datum(interpolatedDataset)
        .attr("fill", "none")
        .attr("stroke", "rgba(0, 0, 0, 1)")
        .attr("stroke-width", 2)
        .attr("d", d3.line()
            .x(d => x(d.year))
            .y(d => y(d.value))
        );

    // Add the points
    svg.append("g")
        .selectAll("dot")
        .data(dataset)
        .enter()
        .append("circle")
        .attr("cx", d => x(d.year))
        .attr("cy", d => y(d.value))
        .attr("r", isCompact ? 3 : 5)
        .attr("fill", d => d.value < 0 ? "rgba(38, 137, 13, 1)" : d.value == 0 ? "black" : "rgba(220, 53, 69, 1)")

    // Add the labels
    svg.append("g")
        .selectAll("text")
        .data(dataset)
        .enter()
        .append("text")
        .attr("x", d => x(d.year))
        .attr("y", d => y(d.value))
        .attr("text-anchor", "middle")
        .attr("dy", d => d.value < 0 ? `${1.25 * labelFontScale}rem` : `${-0.5 * labelFontScale}rem`)
        .attr("fill", d => d.value < 0 ? "rgba(38, 137, 13, 1)" : d.value == 0 ? "black" : "rgba(220, 53, 69, 1)")
        .attr("font-family", "Open Sans")
        .attr("font-weight", "bolder")
        .style("font-size", `${.9 * labelFontScale}rem`)
        // Add a white halo behind the text to improve contrast over lines/backgrounds
        .style("paint-order", "stroke")
        .attr("stroke", "white")
        .attr("stroke-width", isCompact ? 2 : 4)
        .attr("stroke-linejoin", "round")
        .attr("stroke-linecap", "round")
        .text(d => numberFormat(d.value));

}

export function showNoImpact(selector: string) {
    const chartContainer = document.getElementById(selector);
    if (!chartContainer) {
        console.error(`Element with selector '${selector}' not found.`);
        return;
    }

    const existingSvg = chartContainer.querySelector('svg');
    if (existingSvg) {
        existingSvg.remove();
    }

    chartContainer.textContent = "No Impact";
    chartContainer.style.display = "flex";
    chartContainer.style.alignItems = "center";
    chartContainer.style.justifyContent = "center";
    chartContainer.style.fontWeight = "600";
}

export function numberFormat(value: number): string {
    // first format the number with d3
    const formatted = d3.format("(.2s")(value);
    // replace 'k' with 'K' for thousands, and 'G' with 'B' for billions
    return formatted.replace('k', 'K').replace('G', 'B');
}
